import { CompanyMarketPositioningType, type EmployeeLevel, FundingRound, type Headcount } from "@prisma/client";
import { type ParsedUrlQuery } from "querystring";
import { type Input } from "~/components/market-data/MarketDataBrowser";
import { JOB_SELECTOR_LIMIT } from "~/components/ui/JobSelector";
import { type AppContext } from "~/lib/context";
import {
  parseArray,
  parseBoolean,
  parseDecimalNumber,
  parseNumber,
  parseNumericArray,
  parseString,
} from "~/lib/queryParams";
import { arrayHasValues } from "~/lib/utils";
import { getDefaultCurrency } from "~/services/currency";
import { type EmployeeStatsMeasure } from "~/services/employee-stats/utils";
import { getDefaultLocations } from "~/services/employee/employeeLocation";
import { getHeadcountRange, mapRangeToHeadcount } from "~/services/headcount";
import { getDefaultBenchmarkLevelIds, getDefaultLevels } from "~/services/market-data/market-data-filters/utils";
import { DefaultTargetPercentiles, generatePercentileBounds } from "~/services/percentiles";

export const DEFAULT_MARKET_DATA_MEASURE = "totalCash";

type ParseQueryStringOptions = {
  canAccessFiguresAiV2: boolean;
  companyFiltersWithMultipleValuesEnabled: boolean;
  canByPassJobSelectorLimit: boolean;
  useTestData: boolean;
};

const mapFundingRoundsForInput = (
  input: { fundingRounds: FundingRound[] },
  options: { canAccessFiguresAiV2: boolean }
) => {
  if (!options.canAccessFiguresAiV2) return input.fundingRounds;

  const lastFundingRound = input.fundingRounds[0];
  if (!lastFundingRound) return [];

  // Limit the number of funding rounds to a single value,
  // except for the aggregated option 'Series C, Series D, Series E' option,
  // to make it work with the selector
  const seriesCDE: FundingRound[] = [FundingRound.SERIES_C, FundingRound.SERIES_D, FundingRound.SERIES_E];
  if (seriesCDE.includes(lastFundingRound)) return seriesCDE;

  return [lastFundingRound];
};

export const parseQueryString = async (
  ctx: AppContext,
  query: ParsedUrlQuery,
  options: ParseQueryStringOptions
): Promise<Input> => {
  const multipleFiltersEnabled = options.companyFiltersWithMultipleValuesEnabled || !options.canAccessFiguresAiV2;

  // Default input
  const defaultLocations = await getDefaultLocations(ctx);
  const input: Input = {
    currency: await getDefaultCurrency(ctx),
    measure: DEFAULT_MARKET_DATA_MEASURE,
    levels: !ctx.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS ? getDefaultLevels(ctx.user) : [],
    benchmarkLevelIds: ctx.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS ? getDefaultBenchmarkLevelIds(ctx.user) : [],
    useBenchmarkLevelsInShare: false,
    mergeAdvancedLevels: !ctx.user?.company.useAdvancedLevels,
    showCompanyEmployees: true,
    market: {
      fundingRounds: [],
      headcount: null,
      industries: [],
      industry: null,
      jobIds: [],
      labels: [],
      locations: defaultLocations,
      size: { min: null, max: null },
    },
    compare: {
      enabled: false,
      fundingRounds: [],
      headcount: null,
      industries: [],
      industry: null,
      jobIds: [],
      labels: [],
      locations: defaultLocations,
      size: { min: null, max: null },
    },
    percentiles: DefaultTargetPercentiles,
  };

  // Override the input with the company persisted preferences
  if (ctx.user?.company.globalTargetPercentile) {
    input.percentiles = generatePercentileBounds({
      targetPercentile: ctx.user.company.globalTargetPercentile,
      rangeWidth: ctx.user.company.percentileRangeWidth,
    });
  }

  // Only here to prevent people to do some URLs tricks.
  const jobSelectorLimit = options.canByPassJobSelectorLimit ? undefined : JOB_SELECTOR_LIMIT;

  // Override the input with the provided query string parameters
  const parsedQuery = {
    presetId: parseNumber(query, "preset-id"),
    currency: parseString(query, "currency"),
    measure: parseString(query, "measure"),
    levels: !ctx.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS ? parseArray<EmployeeLevel>(query, "levels") : [],
    benchmarkLevelIds: ctx.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS ? parseNumericArray(query, "benchmark-levels") : [],
    showCompanyEmployees: parseBoolean(query, "show-company-employees"),
    market: {
      fundingRounds: parseArray<FundingRound>(query, "funding-rounds"),
      headcount: parseString<Headcount>(query, "headcount"),
      industriesIds: parseNumericArray(query, "industries"),
      industryId: parseNumber(query, "industry"),
      jobsIds: parseArray(query, "jobs").slice(0, jobSelectorLimit),
      labelsIds: parseNumericArray(query, "labels"),
      locationsIds: parseNumericArray(query, "locations"),
      minEmployeesCount: parseNumber(query, "size.min"),
      maxEmployeesCount: parseNumber(query, "size.max"),
      minFunding: parseNumber(query, "funding.min"),
      maxFunding: parseNumber(query, "funding.max"),
    },
    compare: {
      enabled: parseBoolean(query, "compare-with-jobs"),
      fundingRounds: parseArray<FundingRound>(query, "x.funding-rounds"),
      headcount: parseString<Headcount>(query, "x.headcount"),
      industriesIds: parseNumericArray(query, "x.industries"),
      industryId: parseNumber(query, "x.industry"),
      jobsIds: parseArray(query, "x.jobs").slice(0, jobSelectorLimit),
      labelsIds: parseNumericArray(query, "x.labels"),
      locationsIds: parseNumericArray(query, "x.locations"),
      minEmployeesCount: parseNumber(query, "x.size.min"),
      maxEmployeesCount: parseNumber(query, "x.size.max"),
      minFunding: parseNumber(query, "x.funding.min"),
      maxFunding: parseNumber(query, "x.funding.max"),
    },
    percentiles: {
      low: parseDecimalNumber(query, "percentile.low"),
      middle: parseDecimalNumber(query, "percentile.middle"),
      high: parseDecimalNumber(query, "percentile.high"),
    },
  };

  if (parsedQuery.presetId) {
    input.presetId = parsedQuery.presetId;
  }

  if (parsedQuery.currency) {
    input.currency = await ctx.prisma.currency.findUniqueOrThrow({ where: { code: parsedQuery.currency } });
  }

  if (parsedQuery.measure) {
    input.measure = parsedQuery.measure as EmployeeStatsMeasure;
  }

  if (parsedQuery.levels.length) {
    input.levels = parsedQuery.levels;
  }

  if (arrayHasValues(parsedQuery.benchmarkLevelIds)) {
    input.benchmarkLevelIds = parsedQuery.benchmarkLevelIds;
  }

  // Market
  if (parsedQuery.market.jobsIds.length) {
    input.market.jobIds = parsedQuery.market.jobsIds;
  }

  if (parsedQuery.market.locationsIds.length) {
    input.market.locations = await ctx.prisma.employeeLocation.findMany({
      include: { country: true },
      where: { id: { in: parsedQuery.market.locationsIds } },
    });
  }

  if (options.canAccessFiguresAiV2) {
    if (parsedQuery.market.headcount !== null) {
      input.market.headcount = parsedQuery.market.headcount;
    } else if (parsedQuery.market.minEmployeesCount !== null || parsedQuery.market.maxEmployeesCount !== null) {
      // Handle query string backward compatibility, i.e. map deprecated company size min/max to appropriate headcount
      input.market.headcount = mapRangeToHeadcount({
        min: parsedQuery.market.minEmployeesCount,
        max: parsedQuery.market.maxEmployeesCount,
      });
    }
  } else {
    if (parsedQuery.market.minEmployeesCount !== null || parsedQuery.market.maxEmployeesCount !== null) {
      input.market.size.min = parsedQuery.market.minEmployeesCount;
      input.market.size.max = parsedQuery.market.maxEmployeesCount;
    } else if (parsedQuery.market.headcount !== null) {
      input.market.size = getHeadcountRange(parsedQuery.market.headcount);
    }
  }

  if (parsedQuery.market.fundingRounds.length) {
    input.market.fundingRounds = mapFundingRoundsForInput(parsedQuery.market, options);
  }

  if (arrayHasValues(parsedQuery.market.labelsIds)) {
    input.market.labels = await ctx.prisma.companyTag.findMany({ where: { id: { in: parsedQuery.market.labelsIds } } });
  }

  if (!multipleFiltersEnabled) {
    if (parsedQuery.market.industryId !== null) {
      input.market.industry = await ctx.prisma.companyTag.findUnique({ where: { id: parsedQuery.market.industryId } });
    } else if (arrayHasValues(parsedQuery.market.industriesIds)) {
      const industryId = parsedQuery.market.industriesIds[0];

      if (industryId) input.market.industry = await ctx.prisma.companyTag.findUnique({ where: { id: industryId } });
    }
  } else {
    if (arrayHasValues(parsedQuery.market.industriesIds)) {
      input.market.industries = await ctx.prisma.companyTag.findMany({
        where: { id: { in: parsedQuery.market.industriesIds } },
      });
    } else if (parsedQuery.market.industryId !== null) {
      input.market.industries = await ctx.prisma.companyTag.findMany({
        where: { id: { in: [parsedQuery.market.industryId] } },
      });
    }
  }

  // Compare

  if (parsedQuery.showCompanyEmployees === false) {
    input.showCompanyEmployees = parsedQuery.showCompanyEmployees;
  }

  if (parsedQuery.compare.enabled) {
    input.compare.enabled = parsedQuery.compare.enabled;
  }

  if (parsedQuery.compare.jobsIds.length) {
    input.compare.jobIds = parsedQuery.compare.jobsIds;
  }

  if (parsedQuery.compare.locationsIds.length) {
    input.compare.locations = await ctx.prisma.employeeLocation.findMany({
      include: { country: true },
      where: { id: { in: parsedQuery.compare.locationsIds } },
    });
  }

  if (options.canAccessFiguresAiV2) {
    if (parsedQuery.compare.headcount !== null) {
      input.compare.headcount = parsedQuery.compare.headcount;
    } else if (parsedQuery.compare.minEmployeesCount !== null || parsedQuery.compare.maxEmployeesCount !== null) {
      // Handle query string backward compatibility, i.e. map deprecated company size min/max to appropriate headcount
      input.compare.headcount = mapRangeToHeadcount({
        min: parsedQuery.compare.minEmployeesCount,
        max: parsedQuery.compare.maxEmployeesCount,
      });
    }
  } else {
    if (parsedQuery.compare.minEmployeesCount !== null || parsedQuery.compare.maxEmployeesCount !== null) {
      input.compare.size.min = parsedQuery.compare.minEmployeesCount;
      input.compare.size.max = parsedQuery.compare.maxEmployeesCount;
    } else if (parsedQuery.compare.headcount !== null) {
      input.compare.size = getHeadcountRange(parsedQuery.compare.headcount);
    }
  }

  if (parsedQuery.compare.fundingRounds.length) {
    input.compare.fundingRounds = mapFundingRoundsForInput(parsedQuery.compare, options);
  }

  if (arrayHasValues(parsedQuery.compare.labelsIds)) {
    input.compare.labels = await ctx.prisma.companyTag.findMany({
      where: { id: { in: parsedQuery.compare.labelsIds } },
    });
  }

  if (!multipleFiltersEnabled) {
    if (parsedQuery.compare.industryId !== null) {
      input.compare.industry = await ctx.prisma.companyTag.findUnique({
        where: { id: parsedQuery.compare.industryId },
      });
    } else if (arrayHasValues(parsedQuery.compare.industriesIds)) {
      const industryId = parsedQuery.compare.industriesIds[0];

      if (industryId) input.compare.industry = await ctx.prisma.companyTag.findUnique({ where: { id: industryId } });
    }
  } else {
    if (arrayHasValues(parsedQuery.compare.industriesIds)) {
      input.compare.industries = await ctx.prisma.companyTag.findMany({
        where: { id: { in: parsedQuery.compare.industriesIds } },
      });
    } else if (parsedQuery.compare.industryId !== null) {
      input.compare.industries = await ctx.prisma.companyTag.findMany({
        where: { id: { in: [parsedQuery.compare.industryId] } },
      });
    }
  }

  // Percentiles
  if (parsedQuery.percentiles.low !== null) {
    input.percentiles.low = parsedQuery.percentiles.low;
  }

  if (parsedQuery.percentiles.middle !== null) {
    input.percentiles.middle = parsedQuery.percentiles.middle;
  }

  if (parsedQuery.percentiles.high !== null) {
    input.percentiles.high = parsedQuery.percentiles.high;
  }

  // Default filters
  if (!ctx.user?.company.marketPositioningId) {
    return input;
  }

  const defaultFilter = await ctx.prisma.marketPositioning.findUniqueOrThrow({
    where: { id: ctx.user.company.marketPositioningId },
  });

  if (defaultFilter.type === CompanyMarketPositioningType.SIMILAR_HEADCOUNT) {
    input.market.headcount = defaultFilter.headcount;
  }

  if (defaultFilter.type === CompanyMarketPositioningType.SIMILAR_FUNDING_STAGE && defaultFilter.fundingRounds) {
    input.market.fundingRounds = defaultFilter.fundingRounds;
  }

  if (defaultFilter.type === CompanyMarketPositioningType.SPECIFIC_INDUSTRY && defaultFilter.industryId) {
    input.market.industries = await ctx.prisma.companyTag.findMany({ where: { id: defaultFilter.industryId } });
  }

  return input;
};
